import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppAuth } from '../../hooks/useAppAuth';
import { listChatThreads } from '../../services/data.store';
import type { ChatThread } from '../../services/data.store';
import { Loader2Icon, MessageSquareIcon, PlusIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 25;

interface ConversationHistoryProps {
  activeThreadId?: string;
  onSelectThread?: (threadId: string) => void;
  onNewChat?: () => void;
  agentType?: string;
  agentTypes?: string[];
  baseUrl?: string;
}

export const ConversationHistory: React.FC<ConversationHistoryProps> = ({
  activeThreadId,
  onSelectThread,
  onNewChat,
  agentType = 'analyst',
  agentTypes,
  baseUrl = '/ellie',
}) => {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // Pages already loaded; the next "Load more" fetches page `pagesLoaded`.
  const [pagesLoaded, setPagesLoaded] = useState(1);
  const navigate = useNavigate();
  const { user } = useAppAuth();

  const sortByUpdated = (list: ChatThread[]) =>
    [...list].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  // Fetch one page. When the sidebar spans multiple agent types (e.g. ellie +
  // analyst) they're queried together as one `agent_type IN (...)` request, so
  // a page is 25 threads total across types — not 25 per type.
  const fetchPage = useCallback(
    async (page: number) => {
      const userId = user?.id || undefined;
      const typesToFetch = agentTypes || [agentType];
      return listChatThreads(typesToFetch, PAGE_SIZE, userId, page * PAGE_SIZE);
    },
    [user?.id, agentType, agentTypes]
  );

  const loadThreads = useCallback(async () => {
    try {
      setIsLoading(true);
      const { threads: first, hasMore: more } = await fetchPage(0);
      setThreads(sortByUpdated(first));
      setHasMore(more);
      setPagesLoaded(1);
    } catch (error) {
      console.error('Failed to load chat threads:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchPage]);

  const handleLoadMore = useCallback(async () => {
    try {
      setIsLoadingMore(true);
      const { threads: next, hasMore: more } = await fetchPage(pagesLoaded);
      setThreads((prev) => {
        // Dedupe by thread_id in case a thread's updated_at shifted it across
        // a page boundary between fetches.
        const seen = new Set(prev.map((t) => t.thread_id));
        return sortByUpdated([...prev, ...next.filter((t) => !seen.has(t.thread_id))]);
      });
      setHasMore(more);
      setPagesLoaded((n) => n + 1);
    } catch (error) {
      console.error('Failed to load more chat threads:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [fetchPage, pagesLoaded]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const handleSelectThread = (thread: ChatThread) => {
    if (onSelectThread) {
      onSelectThread(thread.thread_id);
    } else {
      const url = thread.agent_type && thread.agent_type !== agentType
        ? `${baseUrl}/conversation/${thread.thread_id}?agent=${thread.agent_type}`
        : `${baseUrl}/conversation/${thread.thread_id}`;
      navigate(url);
    }
  };

  const handleNewChat = () => {
    if (onNewChat) {
      onNewChat();
    } else {
      navigate(baseUrl);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="h-full flex flex-col border-l bg-background">
      <div className="flex-shrink-0 p-4 border-b">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <MessageSquareIcon className="h-4 w-4" />
            Your Chats
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNewChat}
            className="h-7 px-2"
          >
            <PlusIcon className="h-3 w-3 mr-1" />
            New
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && threads.length === 0 && (
          <div className="text-center py-8 px-4">
            <MessageSquareIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No previous conversations found.</p>
          </div>
        )}
        {!isLoading && threads.length > 0 && (
          <div className="divide-y">
            {threads.map((thread) => (
              <div
                key={thread.thread_id}
                className={cn(
                  'p-3 cursor-pointer transition-colors hover:bg-accent',
                  activeThreadId === thread.thread_id && 'bg-accent border-l-2 border-l-primary'
                )}
                onClick={() => handleSelectThread(thread)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {thread.name || 'New Chat'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatTimeAgo(thread.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {!isLoading && hasMore && (
          <div className="p-3">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <Loader2Icon className="h-4 w-4 animate-spin" />
              ) : (
                'Load more'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

